import * as React from "react";
import {useEffect, useLayoutEffect, useMemo, useRef, useState} from "react";
import {useBVH} from "@react-three/drei";
import {useLoader, useThree} from "@react-three/fiber";
import {IFCLoader} from "web-ifc-three";
import {MeshLambertMaterial} from "three";

export interface IFCModelProps {
    url: string,
    createNote: any,
    showBuildingStories: any,
    showIFCSpaces: any,
    directCamera: any,
    highlightedIDs: number[]
}

function IFCModel({url, createNote, showBuildingStories, directCamera, highlightedIDs, showIFCSpaces}: IFCModelProps) {
    const mesh: any = useRef();
    useBVH(mesh);
    const [ifcCategoryOnExpressIDs, setIfcCategoryOnExpressIDs] = useState<any>({});
    const [ifcCategorySubsets, setIfcCategorySubsets] = useState<any>({});
    const camera: any = useThree((state) => state.camera);
    const scene: any = useThree((state) => state.scene);
    const raycaster: any = useThree((state) => state.raycaster);
    const ifc: any = useLoader(IFCLoader, url, (ifcLoader: any) => {
        ifcLoader.ifcManager.setWasmPath("../../wasm/");
    });
    const highlightedMaterial = useMemo(() => new MeshLambertMaterial({
        transparent: true,
        opacity: 0.6,
        color: 0xff88ff,
        depthTest: false,
    }), [])

    useEffect(() => {
        const manager = ifc.ifcManager;
        manager.createSubset({
            modelID: 0,
            ids: highlightedIDs,
            scene: scene,
            material: highlightedMaterial,
            removePrevious: true,
            customID: "highlightedMaterial"
        });
        return () => manager.removeSubset({
            modelID: 0,
            material: highlightedMaterial,
            customID: "highlightedMaterial"
        });
    }, [ifc, scene, highlightedIDs, highlightedMaterial]);

    useEffect(() => {
        const manager = ifc.ifcManager;

        const parseIFCCategories = async () => {
            const ifcCategoryOnExpressIDs: any = {};
            const ifcCategorySubsets: any = {};
            for (const [key, value] of Object.entries(manager.typesMap)) {
                const category: any = value;
                const categoryVal: number = parseInt(key);
                const slabsIDs: number[] = await manager.getAllItemsOfType(0, categoryVal, false);
                if (slabsIDs.length > 0) {
                    ifcCategoryOnExpressIDs[category] = slabsIDs;
                    ifcCategorySubsets[category] = manager.createSubset({
                        modelID: 0,
                        ids: slabsIDs,
                        scene: scene,
                        customID: category
                    });
                }
            }
            ifcCategorySubsets["IFCSPACE"] && ifcCategorySubsets["IFCSPACE"].removeFromParent();

            const ifcBuildingStoryIDs = ifcCategoryOnExpressIDs["IFCBUILDINGSTOREY"];
            const buildingStories: any[] = [];
            for (let i = 0; i < ifcBuildingStoryIDs.length; i++) {
                const id = ifcBuildingStoryIDs[i];
                const buildingStory = await manager.getItemProperties(0, id);
                buildingStories.push({
                    name: buildingStory?.Name?.value ?? '',
                    elevation: buildingStory?.Elevation?.value
                });
            }
            showBuildingStories(buildingStories);


            const ifcSpaceIDs = ifcCategoryOnExpressIDs["IFCSPACE"] ?? [];
            const ifcSpaces: any[] = [];
            for (let i = 0; i < ifcSpaceIDs.length; i++) {
                const id = ifcSpaceIDs[i];
                const buildingSpace = await manager.getItemProperties(0, id);
                ifcSpaces.push({
                    name: buildingSpace?.Name?.value && buildingSpace?.LongName?.value ? buildingSpace.LongName.value + ' ' + buildingSpace.Name.value : '',
                    expressID: id
                });
            }
            showIFCSpaces(ifcSpaces);

            setIfcCategorySubsets(ifcCategorySubsets);
            setIfcCategoryOnExpressIDs(ifcCategoryOnExpressIDs);
        }
        parseIFCCategories();


    }, [ifc, showBuildingStories, scene, showIFCSpaces]);

    useLayoutEffect(() => directCamera(ifc), [ifc, directCamera]);

    return (<group>
        {Object.entries(ifcCategorySubsets).map(([key, value], index) =>
            key !== "IFCSPACE" ?
                <primitive key={index} object={value}
                           onDoubleClick={(e: any) => {
                               createNote(e, camera, raycaster);
                           }}
                /> : null
        )}
    </group>)
}

export default IFCModel;
